import { useState } from "react";
import { Send, HelpCircle, Star, MessageSquare, ExternalLink, ChevronDown } from "lucide-react";
import { Footer } from "../../components/ui/Footer";
import { FeedbackModal } from "../../components/student/FeedbackModal";
import { useStudent } from "../../context/StudentContext";

const FAQS = [
  {
    q: "How do I access past exams and study materials?",
    a: "Select your freshman course from the Study page to view curated modules, chapter quizzes, short notes, and previous semester exams.",
  },
  {
    q: "How does premium membership work?",
    a: "Free accounts can practice the first exam in each category with 5 questions. Premium accounts have full, unlimited access to all exams, quizzes, and short notes.",
  },
  {
    q: "How can I pay using Telebirr or CBE?",
    a: "Open the upgrade dialog, complete your transfer via Telebirr or CBE, and provide your transaction reference number. Accounts are usually activated promptly.",
  },
  {
    q: "What should I do if something is not working?",
    a: "You can reach out directly to our official Telegram support bot. Our team assists with account questions, payments, and platform feedback.",
  },
];

export function HelpPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const { openUpgrade, isPremium } = useStudent();

  return (
    <div className="space-y-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold font-display text-foreground">Help &amp; Support</h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Answers to common questions and direct assistance for your studies.
          </p>
        </div>

        {/* Telegram Direct Support Card */}
        <div className="bg-card rounded-sm border border-border p-5 sm:p-6 space-y-3.5 shadow-xs">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xs bg-info/10 text-info border border-info/20 flex items-center justify-center shrink-0">
              <Send size={18} />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base text-foreground">Telegram Support Bot</h3>
              <p className="text-xs text-muted-foreground">Official assistance for account, payment, and course inquiries</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Need help with your account or receipts? Message our verified Telegram support bot for assistance.
          </p>
          <div className="pt-1">
            <a
              href="https://t.me/Soorgaa_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary px-4 py-2 text-xs font-semibold rounded-xs inline-flex items-center gap-1.5"
            >
              <Send size={13} />
              <span>Open Telegram Support</span>
              <ExternalLink size={11} className="opacity-80" />
            </a>
          </div>
        </div>

        {/* Membership & Feedback Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!isPremium && (
            <div className="bg-card rounded-sm border border-border p-4 space-y-2 shadow-xs">
              <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
                <Star size={14} />
                <span>Premium Membership</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Unlock full past exam archives and all questions.
              </p>
              <button
                type="button"
                onClick={() => openUpgrade()}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Upgrade now &rarr;
              </button>
            </div>
          )}

          <div className="bg-card rounded-sm border border-border p-4 space-y-2 shadow-xs">
            <div className="flex items-center gap-2 text-foreground font-bold text-xs uppercase tracking-wider">
              <MessageSquare size={14} className="text-primary" />
              <span>Feedback &amp; Rating</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Help us improve Soorgaa with your suggestions.
            </p>
            <button
              type="button"
              onClick={() => setShowFeedback(true)}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Share feedback &rarr;
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-card rounded-sm border border-border p-5 sm:p-6 space-y-4 shadow-xs">
          <div className="flex items-center gap-2">
            <HelpCircle size={16} className="text-primary" />
            <h3 className="font-bold text-sm sm:text-base text-foreground">Frequently Asked Questions</h3>
          </div>
          <div className="divide-y divide-border/60">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="py-3">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between text-left text-xs sm:text-sm font-semibold text-foreground hover:text-primary transition gap-2"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown
                      size={14}
                      className={`text-muted-foreground transition-transform shrink-0 ${
                        isOpen ? "rotate-180 text-primary" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {faq.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer rendered in Help section */}
      <Footer variant="student" />

      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </div>
  );
}
